// R-17 (remate F) — EL AVISO DEL REINTENTO, UNA SOLA VEZ Y PARA LAS DOS SUPERFICIES.
//
// EL HALLAZGO (smoke v5, reproducido en EN y en ES): tocando "Reintentar" sobre una
// parte roja sin créditos, a veces salía el genérico ("Reintento falló… contacta a
// soporte") en vez del aviso con los números, y el bueno aparecía "de la nada" en
// toques posteriores. No era el 402 cayéndose: era OTRO status vistiéndose de fallo.
//
// POR QUÉ ES INTERMITENTE — el reloj que el usuario no ve. `/documents/{id}/retry`
// abre con `check_rate_limit(user_id + "_retry", limit=3, window=120)`
// (routes/documents.py:2266): TRES llamadas por CADA 120 s, contadas por usuario en la
// base. Del cuarto toque en adelante dentro de esa ventana el backend responde 429
// ANTES de mirar el saldo, así que el 402 con los números ni se calcula. Cuando la
// ventana vence, el toque siguiente vuelve a llegar al 402 y el aviso bueno "regresa
// solo". En un smoke uno toca varias veces seguidas para leer el aviso — justo el
// patrón que agota el cubo. La página de Pendientes mandaba TODO lo que no fuera 402 al
// mismo genérico (PendingPage.js:198, la frontera que COS-G B7 dejó a propósito sin
// mover), y ese copy dice "si esto persiste, contacta a soporte": para un freno de
// ráfaga es mentira — no hay nada roto y esperar SÍ sirve.
//
// El Dashboard ya tenía la clasificación fina (COS-G B1, `_retryErrorToast`), y la
// página de Pendientes no. Esa asimetría entre dos superficies que llaman a la MISMA
// cadena es la causa raíz, así que la clasificación se muda aquí y las dos la usan.
//
// LO ÚNICO QUE CAMBIA POR SUPERFICIE es el copy del 402: en el Panel se habla del
// documento ("Créditos insuficientes: necesitas X, tienes Y") y en Pendientes de la
// parte ("…para reintentar esa parte: necesitas X, tienes Y"). Cero copy nuevo: las
// seis claves ya existen en EN y ES.

/**
 * Traduce un fallo de la cadena /retry → /process al aviso que le toca.
 *
 * @param err   el error de axios
 * @param t     la `t` de la superficie que llama
 * @param opts.insufficient   clave del 402 SIN números (por defecto, la del Panel)
 * @param opts.insufficientN  clave del 402 CON números
 * @param opts.serverDetailFallback  si un `detail` de TEXTO debe pintarse tal cual antes
 *        de caer al genérico. Lo enciende sólo el Panel, que se comporta así desde COS-G;
 *        la página de Pendientes lo deja apagado a propósito (ver PendingPage.js).
 */
export function retryErrorToast(err, t, opts = {}) {
  const {
    insufficient = 'dashboard:toasts.retry_insufficient_credits',
    insufficientN = 'dashboard:toasts.retry_insufficient_credits_n',
    serverDetailFallback = false,
  } = opts;

  const code = err?.response?.status;
  const detail = err?.response?.data?.detail;

  if (code === 402) {
    // COS-G B7 — el 402 del retry YA trae los dos números (documents.py:2333-2338, y los
    // dos de /process: :1557-1562 y :1583-1588). Con ellos el aviso dice qué falta; sin
    // ellos, el copy a secas — un toast con "undefined" sería peor que el genérico.
    const req = detail?.credits_required;
    const have = detail?.credits_available;
    if (typeof req === 'number' && typeof have === 'number') {
      return t(insufficientN, { required: req, available: have });
    }
    return t(insufficient);
  }

  // TANDA H / C-4 — EL TOPE DURO, AHORA POR CÓDIGO. Los DOS topes duros de la cadena
  // (`partial`, documents.py:2286+, y `error`, :2317+) viajan hoy en un 409 con la MISMA
  // razón clasificable. Antes esto no existía: el de `partial` era un 429 que se
  // distinguía LEYENDO LA PROSA del backend, y el de `error` —que ya era 409— caía al
  // genérico porque su `detail` es un objeto. Los dos quedan aquí, y ninguna frase del
  // servidor vuelve a ser el discriminador.
  //
  // Se exige la RAZÓN, no sólo el 409: la misma cadena /retry → /process emite otros
  // 409 que NO son topes (`use_retry`, `use_process_pending`, `duplicate_document`,
  // `duplicate_archived`). Ésos siguen su camino de siempre.
  if (code === 409 && detail && detail.error === 'retry_limit_reached') {
    return t('dashboard:toasts.retry_limit_reached');
  }

  if (code === 429) {
    // COS-G B1 (actualizado por C-4) — EN ESTE ENDPOINT 429 YA SIGNIFICA UNA SOLA COSA:
    // el freno de ráfaga de P-F10 (`check_rate_limit`, documents.py:2266), donde esperar
    // es EXACTAMENTE lo que hay que hacer — muerde antes de tocar el documento. El otro
    // 429 histórico (tope duro de `partial`) se mudó al 409 de arriba.
    //
    // LA VENTANA DE DESPLIEGUE, A PROPÓSITO SIN REGLA ESPECIAL: backend (Render) y
    // frontend (Cloudflare Pages) despliegan por pipelines separados, así que durante
    // unos minutos este frontend puede recibir un tope duro "estilo viejo" en 429. Cae
    // aquí y dice "espera unos minutos": no es el copy ideal para un tope duro, pero es
    // un aviso honesto de espera — NO el genérico de "contacta a soporte" y NO una falla
    // de red. Volver a oler la prosa para cubrir esos minutos reviviría justo la
    // fragilidad que C-4 vino a matar.
    return t('dashboard:toasts.retry_rate_limited');
  }

  if (code === 503) return t('dashboard:toasts.retry_service_unavailable');

  // El `detail` sólo se pinta si es TEXTO. Los rechazos clasificables del backend lo
  // mandan como objeto ({error, ...}): pasarlo tal cual al toast sería pintarle un
  // [object Object] al usuario. Para ésos cae el genérico, que ya dice lo único que
  // queda por hacer: volver a subirlo. (El 409 `retry_limit_reached` ya no llega hasta
  // aquí — lo atiende la rama de código de arriba, C-4.)
  if (serverDetailFallback && typeof detail === 'string' && detail) return detail;

  // El genérico, ahora sí, para lo que de verdad se rompió: red, 5xx, y los 4xx sin
  // relato propio. Un fallo SIN `response` (red caída, o el techo de 10 s de api.js)
  // aterriza aquí, que es donde tiene que aterrizar.
  return t('dashboard:toasts.retry_failed');
}

export default retryErrorToast;
